'use client';

import { useCallback, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { ApiError, accountsApi, itemsApi, transactionsApi, expensesApi, investmentsApi, homeApi, goalsApi, categoriesApi, usersApi, connectorsApi } from '@/lib/api';
import { handleApiError } from '@/lib/upgradeRequired';
import type { TransactionCategoryBatchRequest } from '@/lib/api';
import type { UpdateTransactionDto, CreateGoalDto, UpdateGoalDto } from '@/types';

// A plan-limit 402 is fully explained by the global upgrade dialog — the inline
// error keeps a short pt-BR summary rather than echoing the server body.
function connectionErrorMessage(err: unknown): string {
  if (err instanceof ApiError && err.isLimitExceeded()) {
    return 'Limite de conexões do plano atingido.';
  }
  return err instanceof Error ? err.message : 'Unknown error';
}

/**
 * useFinance hook - provides methods to interact with finance APIs
 *
 * Loading state management:
 * - `loading` is true when ANY operation is in progress
 * - Uses a counter to track concurrent operations (not a boolean)
 * - Safe for parallel API calls - loading stays true until ALL operations complete
 * - `error` captures the last error that occurred
 *
 * Auth error handling:
 * - Uses skipCache to always get fresh tokens
 * - Retries once on auth errors with a fresh token
 */
export function useFinance() {
  const { getToken } = useAuth();
  // Use a counter to track concurrent operations instead of a boolean
  const pendingCountRef = useRef(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startLoading = useCallback(() => {
    pendingCountRef.current += 1;
    setLoading(true);
  }, []);

  const stopLoading = useCallback(() => {
    pendingCountRef.current -= 1;
    if (pendingCountRef.current <= 0) {
      pendingCountRef.current = 0;
      setLoading(false);
    }
  }, []);

  const getBearerToken = useCallback(async (forceRefresh = false): Promise<string> => {
    // Force fresh token by passing skipCache option
    // This helps prevent "Invalid token" errors from stale tokens
    const token = await getToken({ skipCache: forceRefresh });
    if (!token) throw new Error('Not authenticated');
    return token;
  }, [getToken]);

  /**
   * Execute an API call with automatic retry on auth errors
   */
  const withAuthRetry = useCallback(
    async <T>(apiCall: (token: string) => Promise<T>): Promise<T> => {
      try {
        const token = await getBearerToken();
        return await apiCall(token);
      } catch (err) {
        // If it's an auth error, retry once with a fresh token
        if (err instanceof ApiError && err.isAuthError()) {
          console.warn('Auth error detected, retrying with fresh token...');
          const freshToken = await getBearerToken(true);
          return await apiCall(freshToken);
        }
        throw err;
      }
    },
    [getBearerToken],
  );

  const getConnectToken = useCallback(
    async (itemId?: string, redirectUri?: string): Promise<{ accessToken: string; cpf: string }> => {
      startLoading();
      setError(null);
      try {
        const token = await getBearerToken();
        return await itemsApi.getConnectToken(
          token,
          itemId,
          redirectUri,
        );
      } catch (err) {
        // This fetch path predates useAuthedFetcher and bypassed the global 402
        // dialog — a plan-limit refusal surfaced as a raw error string instead of
        // the upgrade dialog every other surface gets.
        handleApiError(err);
        setError(connectionErrorMessage(err));
        throw err;
      } finally {
        stopLoading();
      }
    },
    [getBearerToken, startLoading, stopLoading],
  );

  const fetchItems = useCallback(async () => {
    startLoading();
    setError(null);
    try {
      return await withAuthRetry((token) => itemsApi.getAll(token));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      throw err;
    } finally {
      stopLoading();
    }
  }, [withAuthRetry, startLoading, stopLoading]);

  const fetchConnectorHealth = useCallback(async () => {
    startLoading();
    setError(null);
    try {
      return await withAuthRetry((token) => connectorsApi.getHealth(token));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      throw err;
    } finally {
      stopLoading();
    }
  }, [withAuthRetry, startLoading, stopLoading]);

  const deleteItem = useCallback(
    async (id: string) => {
      startLoading();
      setError(null);
      try {
        const token = await getBearerToken();
        await itemsApi.delete(token, id);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setError(message);
        throw err;
      } finally {
        stopLoading();
      }
    },
    [getBearerToken, startLoading, stopLoading],
  );

  const fetchAccounts = useCallback(async () => {
    startLoading();
    setError(null);
    try {
      const token = await getBearerToken();
      return await accountsApi.getAll(token);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      throw err;
    } finally {
      stopLoading();
    }
  }, [getBearerToken, startLoading, stopLoading]);

  const fetchCreditCardCockpit = useCallback(
    async (from?: Date, to?: Date) => {
      startLoading();
      setError(null);
      try {
        return await withAuthRetry((token) =>
          expensesApi.getCockpit(token, {
            from: from?.toISOString(),
            to: to?.toISOString(),
          }),
        );
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setError(message);
        throw err;
      } finally {
        stopLoading();
      }
    },
    [withAuthRetry, startLoading, stopLoading],
  );

  const fetchInvoiceDetail = useCallback(
    async (
      accountId: string,
      year: number,
      month: number,
      position: 'closing' | 'opening',
      options?: { limit?: number; offset?: number },
    ) => {
      startLoading();
      setError(null);
      try {
        return await withAuthRetry((token) =>
          expensesApi.getInvoiceDetail(token, accountId, year, month, position, options),
        );
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setError(message);
        throw err;
      } finally {
        stopLoading();
      }
    },
    [withAuthRetry, startLoading, stopLoading],
  );

  const markClosedInvoicePaid = useCallback(
    async (accountId: string, year: number, month: number, dueDate: string, fingerprint: string) => {
      startLoading();
      setError(null);
      try {
        return await withAuthRetry((token) =>
          expensesApi.markClosedInvoicePaid(token, accountId, year, month, dueDate, fingerprint),
        );
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setError(message);
        throw err;
      } finally {
        stopLoading();
      }
    },
    [withAuthRetry, startLoading, stopLoading],
  );

  const fetchTransactions = useCallback(
    async (options?: {
      from?: Date;
      to?: Date;
      accountId?: string;
      type?: string;
          paymentMethod?: string;
          isInstallment?: boolean;
          categoryId?: string | string[];
          parentCategoryId?: string | string[];
          q?: string;
          limit?: number;
          offset?: number;
          sortField?: 'date' | 'value' | 'merchant';
          sortOrder?: 'asc' | 'desc';
        }) => {
      startLoading();
      setError(null);
      try {
        const token = await getBearerToken();
        return await transactionsApi.getAll(token, {
          from: options?.from?.toISOString(),
          to: options?.to?.toISOString(),
          accountId: options?.accountId,
          type: options?.type,
          paymentMethod: options?.paymentMethod,
          isInstallment: options?.isInstallment,
          categoryId: options?.categoryId,
          parentCategoryId: options?.parentCategoryId,
          q: options?.q,
          limit: options?.limit,
          offset: options?.offset,
          sortField: options?.sortField,
          sortOrder: options?.sortOrder,
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setError(message);
        throw err;
      } finally {
        stopLoading();
      }
    },
    [getBearerToken, startLoading, stopLoading],
  );

  const fetchTransaction = useCallback(
    async (id: string) => {
      startLoading();
      setError(null);
      try {
        const token = await getBearerToken();
        return await transactionsApi.getOne(token, id);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setError(message);
        throw err;
      } finally {
        stopLoading();
      }
    },
    [getBearerToken, startLoading, stopLoading],
  );

  const updateTransaction = useCallback(
    async (id: string, data: UpdateTransactionDto) => {
      startLoading();
      setError(null);
      try {
        const token = await getBearerToken();
        return await transactionsApi.update(token, id, data);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setError(message);
        throw err;
      } finally {
        stopLoading();
      }
    },
    [getBearerToken, startLoading, stopLoading],
  );

  const previewTransactionCategoryBatch = useCallback(
    async (data: TransactionCategoryBatchRequest) => {
      startLoading();
      setError(null);
      try {
        return await withAuthRetry((token) => transactionsApi.previewCategoryBatch(token, data));
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setError(message);
        throw err;
      } finally {
        stopLoading();
      }
    },
    [withAuthRetry, startLoading, stopLoading],
  );

  const updateTransactionCategoryBatch = useCallback(
    async (data: TransactionCategoryBatchRequest) => {
      startLoading();
      setError(null);
      try {
        return await withAuthRetry((token) => transactionsApi.updateCategoryBatch(token, data));
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setError(message);
        throw err;
      } finally {
        stopLoading();
      }
    },
    [withAuthRetry, startLoading, stopLoading],
  );

  const fetchInvestmentDetail = useCallback(async (id: string, options?: { limit?: number; offset?: number }) => {
    startLoading();
    setError(null);
    try {
      const token = await getBearerToken();
      return await investmentsApi.getDetail(token, id, options);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      throw err;
    } finally {
      stopLoading();
    }
  }, [getBearerToken, startLoading, stopLoading]);

  const fetchHomeSummary = useCallback(async (options?: { year?: number; month?: number; from?: string; to?: string }) => {
    startLoading();
    setError(null);
    try {
      return await withAuthRetry((token) => homeApi.getSummary(token, options));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      throw err;
    } finally {
      stopLoading();
    }
  }, [withAuthRetry, startLoading, stopLoading]);

  const fetchGoalsOverview = useCallback(async () => {
    startLoading();
    setError(null);
    try {
      return await withAuthRetry((token) => goalsApi.getOverview(token));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      throw err;
    } finally {
      stopLoading();
    }
  }, [withAuthRetry, startLoading, stopLoading]);

  const fetchGoalDetail = useCallback(async (id: string) => {
    startLoading();
    setError(null);
    try {
      return await withAuthRetry((token) => goalsApi.getById(token, id));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      throw err;
    } finally {
      stopLoading();
    }
  }, [withAuthRetry, startLoading, stopLoading]);

  const createGoal = useCallback(async (data: CreateGoalDto) => {
    startLoading();
    setError(null);
    try {
      return await withAuthRetry((token) => goalsApi.create(token, data));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      throw err;
    } finally {
      stopLoading();
    }
  }, [withAuthRetry, startLoading, stopLoading]);

  const updateGoal = useCallback(async (id: string, data: UpdateGoalDto) => {
    startLoading();
    setError(null);
    try {
      return await withAuthRetry((token) => goalsApi.update(token, id, data));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      throw err;
    } finally {
      stopLoading();
    }
  }, [withAuthRetry, startLoading, stopLoading]);

  const deleteGoal = useCallback(async (id: string) => {
    startLoading();
    setError(null);
    try {
      return await withAuthRetry((token) => goalsApi.delete(token, id));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      throw err;
    } finally {
      stopLoading();
    }
  }, [withAuthRetry, startLoading, stopLoading]);

  const fetchGoalContributionHistory = useCallback(async (id: string) => {
    startLoading();
    setError(null);
    try {
      return await withAuthRetry((token) => goalsApi.getHistory(token, id));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      throw err;
    } finally {
      stopLoading();
    }
  }, [withAuthRetry, startLoading, stopLoading]);

  const fetchLinkableEntities = useCallback(async () => {
    startLoading();
    setError(null);
    try {
      return await withAuthRetry((token) => goalsApi.getLinkableEntities(token));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      throw err;
    } finally {
      stopLoading();
    }
  }, [withAuthRetry, startLoading, stopLoading]);

  const fetchCategories = useCallback(async (options?: { usedSince?: Date }) => {
    startLoading();
    setError(null);
    try {
      return await withAuthRetry((token) => categoriesApi.getAll(token, options));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      throw err;
    } finally {
      stopLoading();
    }
  }, [withAuthRetry, startLoading, stopLoading]);

  return {
    loading,
    error,
    getConnectToken,
    fetchItems,
    fetchConnectorHealth,
    deleteItem,
    fetchAccounts,
    fetchCreditCardCockpit,
    fetchInvoiceDetail,
    markClosedInvoicePaid,
    fetchTransactions,
    fetchTransaction,
    updateTransaction,
    previewTransactionCategoryBatch,
    updateTransactionCategoryBatch,
    fetchInvestmentDetail,
    fetchHomeSummary,
    fetchGoalsOverview,
    fetchGoalDetail,
    createGoal,
    updateGoal,
    deleteGoal,
    fetchGoalContributionHistory,
    fetchLinkableEntities,
    fetchCategories,
  };
}
